import React, { useEffect, useState } from 'react';
import { NextPageContext } from 'next';
import { useRouter } from 'next/router';
import PageLayout from '@/components/Layouts/PageLayout';
import Container from '@/shared/Container';
import Div from '@/shared/Div';
import ssrErrorHandler from '@/errors/ssrErrorHandler';
import Button from '@/shared/Button';
import { fetchStates, fetchCities, fetchLocations } from '@/actions/fetchLocations';
import useScreen from '@/hooks/useScreen';
import LocationSelector from '@/components/StoreLocator/LocationSelector';
import ServiceLocationsSelector from '@/components/StoreLocator/ServiceLocationsContainer';
import CorporateOfficePage from '@/components/StoreLocator/CorporateOfficePage';
import Heading from '@/shared/Heading';
import { getStaticContent } from '@/actions/ecom/staticContent';
import useTriggerRequest from '@/hooks/useTriggerRequest';
import classNames from 'classnames';

const LOCATION_CATEGORIES = [
  { label: 'Sales Office', id: 'sales-office' },
  { label: 'Eureka Forbes Authorized Service Centers', id: 'authorized-center' },
  { label: 'Franchisee', id: 'franchisee' },
  { label: 'Corporate Office', id: 'corporate-office' },
  { label: 'Franchisee Business Partners', id: 'franchisee-business-partner' },
];

const DEFAULT_CITY = { id: 'all', label: 'Select City' };
const DEFAULT_STATE = { id: 'all', label: 'Select State' };

interface LocationPageProps {
  seo: any;
  page: any;
  type: string;
  initialStates: State[];
  initialLocations: any[];
  googleMapApiKey: string;
}

interface State {
  id: string;
  label: string;
}

const LocationPage: React.FC<LocationPageProps> = ({
  page,
  type,
  initialStates,
  initialLocations,
  googleMapApiKey,
}) => {
  const router = useRouter();
  const { isMobile, isTablet } = useScreen();
  const isSmallScreen = isMobile || isTablet;
  const defaultCategory = LOCATION_CATEGORIES[0].id;
  const [activeTab, setActiveTab] = useState(type || defaultCategory);
  const [states, setStates] = useState<State[]>([DEFAULT_STATE, ...initialStates]);
  const [cities, setCities] = useState<State[]>([DEFAULT_CITY]);
  const [selectedState, setSelectedState] = useState<State | null>(DEFAULT_STATE);
  const [selectedCity, setSelectedCity] = useState<State>(DEFAULT_CITY);
  const [errors, setErrors] = useState({});
  const [showLocations, setShowLocations] = useState(true);
  const [locations, setLocations] = useState(initialLocations);
  const [statesKey, setStatesKey] = useState(0);
  const [citiesKey, setCitiesKey] = useState(0);
  const { isLoading: isLoadingLocations, promisifyTrigger: triggerFetchLocations } =
    useTriggerRequest({
      fetch: fetchLocations,
    });

  const { isLoading: isLoadingCities, promisifyTrigger: triggerFetchCities } = useTriggerRequest({
    fetch: fetchCities,
  });

  const loadCitiesForState = async (stateId: string) => {
    if (stateId === 'all') {
      setCities([DEFAULT_CITY]);
      return;
    }

    try {
      const citiesData = await triggerFetchCities(stateId, type);
      const formattedCities = citiesData.map((city: any) => ({
        id: city.attributes.cityId,
        label: city.attributes.name,
      }));
      setCities([DEFAULT_CITY, ...formattedCities]);
      handleCityChange(DEFAULT_CITY);
    } catch (error) {
      console.error('Error loading cities:', error);
      setErrors({ ...errors, cities: 'Failed to load cities' });
      setCities([DEFAULT_CITY]);
      handleCityChange(DEFAULT_CITY);
    }
  };

  const fetchAllLocations = async (categoryId: any) => {
    try {
      const response = await triggerFetchLocations(categoryId);
      setShowLocations(true);
      setLocations(response.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
      setShowLocations(true);
      setLocations([]);
    }
  };

  useEffect(() => {
    if (type && type !== activeTab) {
      setActiveTab(type);
      setLocations(initialLocations);
      setStates([DEFAULT_STATE, ...initialStates]);
      setSelectedState(DEFAULT_STATE);
      setSelectedCity(DEFAULT_CITY);
      setCities([DEFAULT_CITY]);
    }
  }, [type, initialLocations, initialStates]);

  const handleCategoryChange = (newCategory: string) => {
    if (newCategory !== activeTab) {
      router.push(`/store-locator/${newCategory}`, undefined, {
        shallow: false,
      });

      // Force re-render of dropdowns by changing keys
      setStatesKey((prev) => prev + 1);
      setCitiesKey((prev) => prev + 1);

      // Reset state selections
      setSelectedState(DEFAULT_STATE);
      setSelectedCity(DEFAULT_CITY);
    }
  };

  const handleStateChange = async (state: State) => {
    setSelectedState(state);
    setSelectedCity(DEFAULT_CITY);
    await loadCitiesForState(state.id);
  };

  const handleCityChange = (city: State) => {
    setSelectedCity(city);
  };

  const handleReset = async () => {
    try {
      const activeCategory = LOCATION_CATEGORIES.find((category) => category.id === activeTab);
      const categoryId = activeCategory?.id;
      setSelectedState(DEFAULT_STATE);
      setSelectedCity(DEFAULT_CITY);
      setCities([DEFAULT_CITY]);
      await fetchAllLocations(categoryId);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const handleSubmit = async () => {
    if (!selectedState || selectedState.id === 'all') return;

    try {
      const activeCategory = LOCATION_CATEGORIES.find((category) => category.id === activeTab);
      const categoryId = activeCategory?.id;
      const stateId = selectedState?.id;
      const cityId = selectedCity.id === 'all' ? '' : selectedCity.id;

      const response = await triggerFetchLocations(categoryId, stateId, cityId);

      if (response.error || !response.data) {
        setShowLocations(true);
        setLocations([]);
      } else {
        setShowLocations(true);
        setLocations(response.data);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
      setShowLocations(true);
      setLocations([]);
    }
  };

  const renderLocationContent = () => {
    if (activeTab === 'corporate-office') {
      return <CorporateOfficePage initialLocations={locations} googleMapApiKey={googleMapApiKey} />;
    }

    if (activeTab === 'authorized-center') {
      return (
        <Div
          className={classNames('relative z-10 ', {
            'bg-cs-secondary': activeTab === 'authorized-center',
          })}
        >
          <Container className="">
            <ServiceLocationsSelector
              key={`category-${statesKey}`}
              isAuthorizedCenter={activeTab === 'authorized-center'}
              categoryId={activeTab}
              states={states}
              cities={cities}
              selectedState={selectedState}
              showLocations={showLocations}
              locations={locations}
              isMobile={isMobile}
              isTablet={isTablet}
              onStateChange={handleStateChange}
              onCityChange={handleCityChange}
              onReset={handleReset}
              onSubmit={handleSubmit}
              googleMapApiKey={googleMapApiKey}
            />
          </Container>
        </Div>
      );
    }

    return (
      <Div
        className={classNames('relative z-10 ', {
          'bg-white': activeTab !== 'authorized-center',
          'bg-cs-secondary': activeTab === 'authorized-center',
        })}
      >
        <Container>
          <LocationSelector
            key={`category-${statesKey}`}
            states={states}
            cities={cities}
            selectedState={selectedState}
            showLocations={showLocations}
            locations={locations}
            isMobile={isSmallScreen} // Updated to use combined screen size check
            onStateChange={handleStateChange}
            onCityChange={handleCityChange}
            onReset={handleReset}
            onSubmit={handleSubmit}
            categoryId={activeTab}
          />
        </Container>
      </Div>
    );
  };

  return (
    <PageLayout seo={page.seo}>
      <Div
        className={classNames('relative', {
          'bg-cs-secondary': activeTab === 'authorized-center',
        })}
      >
        <Div
          className={classNames('sticky top-0 z-20', {
            'bg-cs-primary': activeTab !== 'authorized-center',
            'bg-cs-secondary': activeTab === 'authorized-center',
          })}
        >
          <Container className="pt-s5 pb-s5 ">
            <Div className="mb-s6">
              <Heading level={1} className="text-h-1">
                Locate Us
              </Heading>
            </Div>
            <Div className="relative w-full h-fit-content">
              <Div className="flex overflow-x-auto whitespace-nowrap gap-[20px] md:gap-s10 border-b border-neutral-300">
                {LOCATION_CATEGORIES.map((item) => (
                  <Button
                    key={item.id}
                    theme="icon"
                    onClick={() => handleCategoryChange(item.id)}
                    className={`border-b-2 pb-s3 md:pb-s2 text-body-xl md:text-body-xl flex items-center justify-center md:min-w-[120px] ${
                      activeTab === item.id
                        ? 'border-blue-400 text-blue-400 font-bold'
                        : 'border-transparent text-cti-tertiary'
                    }`}
                  >
                    {item.label}
                  </Button>
                ))}
              </Div>
            </Div>
          </Container>
        </Div>
        {renderLocationContent()}
      </Div>
    </PageLayout>
  );
};

export const getServerSideProps = async (context: NextPageContext) => {
  try {
    const { query } = context;
    const type = query.type || LOCATION_CATEGORIES[0].id;
    const page = await getStaticContent('/store-locator');
    const googleMapApiKey = process.env.GOOGLE_MAP_API_KEY;

    const [statesData, locationsResponse] = await Promise.all([
      fetchStates(type as string),
      fetchLocations(type),
    ]);

    const formattedStates = statesData.map((state: any) => ({
      id: state.attributes.stateId,
      label: state.attributes.name,
    }));

    return {
      props: {
        page,
        type,
        initialStates: formattedStates,
        initialLocations: locationsResponse.data || [],
        googleMapApiKey,
      },
    };
  } catch (error) {
    return ssrErrorHandler(error, context);
  }
};

export default LocationPage;
